import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Award,
  Bell,
  Bookmark,
  BookmarkCheck,
  CalendarDays,
  Camera,
  CircleUser,
  GraduationCap,
  IdCard,
  KeyRound,
  Loader2,
  LogOut,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  UserRound,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { universityList } from "@/assets/universityList";
import { COUNTRY_CODES } from "@/assets/countryCodes";
import { useStudentAccount } from "@/hooks/useStudentAccount";
import { openChatRoom } from "@/services/middleware/chatHelper";
import {
  formatDateRange,
  formatTimeRange,
  localized,
  pickLocalizedName,
  toImageList,
  withMomentLocale,
} from "@/utils/eventFormat";
import { GetSubscriptedEventsAPI } from "@/services/api/user/events";
import type { StudentEvent } from "@/services/api/user/events";
import { CountSubscriptedAPI } from "@/services/api/user/countSubscripted";
import { FollowOrganizerOrNotAPI } from "@/services/api/user/followOrganizer";
import { SubscribeOrNotAPI } from "@/services/api/user/subscribeEvent";
import StudentAvatar from "@/components/student/StudentAvatar";
import StudentVerificationCard from "@/components/student/StudentVerificationCard";
import SocietyAvatar from "@/components/SocietyAvatar";
import AdminEmptyState from "@/components/AdminEmptyState";
import Modal from "@/components/Modal";

const FIELD_ICONS: Record<string, LucideIcon> = {
  student_card_number: IdCard,
  year_of_study: GraduationCap,
  phone_num: Phone,
  gender: UserRound,
  identity: Users,
  bachelor_degree: Award,
};

type AccountTab = "societies" | "following" | "subscription";

/** `getSubscriptedList` pages ten events at a time, capped so a heavy
 * subscriber cannot spin the page through hundreds of requests. */
const SUBSCRIPTION_PAGE_SIZE = 10;
const SUBSCRIPTION_MAX_PAGES = 5;

const FIELD_LABELS: Record<string, string> = {
  student_card_number: "Student Card",
  year_of_study: "Year Of Study",
  phone_num: "Phone Number",
  gender: "Gender",
  identity: "Identity",
  bachelor_degree: "Bachelor degree",
};

/**
 * Student account — the web port of the RN `AccountScreen` / `SettingScreen` /
 * `EditProfileScreen` / `ChangePasswordScreen`. Shows the Redux profile, the
 * edit-profile and change-password forms, and an Instagram-style tabbed
 * section holding the societies the student belongs to, the societies they
 * follow and the events they subscribed to.
 */
const StudentAccountPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [chatOpening, setChatOpening] = useState(false);
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  withMomentLocale(i18n.language);
  const {
    student,
    societies,
    pendingSocieties,
    followings,
    followingCount,
    subscribedCount,
    loading,
    editVisible,
    editUsername,
    editRegionCode,
    editPhone,
    editIcon,
    editInvalid,
    savingProfile,
    editPhoneVerifyVisible,
    pendingPhone,
    editPhoneCode,
    editPhoneError,
    sendingPhoneCode,
    verifyingPhoneCode,
    passwordVisible,
    oldPassword,
    newPassword,
    passwordInvalid,
    savingPassword,
    alertVisible,
    alertText,
    logoutVisible,
    setEditUsername,
    setEditRegionCode,
    setEditPhone,
    setEditPhoneCode,
    setEditPhoneError,
    setEditInvalid,
    setEditVisible,
    setEditPhoneVerifyVisible,
    setPasswordVisible,
    setOldPassword,
    setNewPassword,
    setPasswordInvalid,
    setLogoutVisible,
    openEditProfile,
    pickImage,
    saveProfile,
    resendProfilePhoneCode,
    verifyPhoneAndSave,
    openChangePassword,
    savePassword,
    hideAlert,
    handleLogout,
  } = useStudentAccount();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<AccountTab>("societies");
  const [tabError, setTabError] = useState("");
  // Follow / subscribe state of the tab lists. The rows of both tabs start out
  // true (they are the lists of what the student follows / subscribed to) and
  // are flipped optimistically, so a row can be toggled back without leaving
  // the page.
  const [followingMap, setFollowingMap] = useState<Record<string, boolean>>({});
  const [pendingSocietyId, setPendingSocietyId] = useState<string | null>(null);
  const [subscribedEvents, setSubscribedEvents] = useState<StudentEvent[]>([]);
  const [subscribedMap, setSubscribedMap] = useState<Record<string, boolean>>(
    {},
  );
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);
  const [subscribedLoading, setSubscribedLoading] = useState(true);
  // The hook owns the initial counts; the page tracks the deltas of the
  // follow / subscribe toggles it performs itself.
  const [followingTotal, setFollowingTotal] = useState(followingCount);
  const [subscribedTotal, setSubscribedTotal] = useState(subscribedCount);

  const isSocietyMember = societies.length > 0;

  const displayName = student?.username || student?.university_email || "";

  // University is stored as a code (`hku`) which doubles as a translation key.
  const universityLabel = (code?: string | null) => {
    if (!code) return "";
    const fallback = universityList.find((item) => item.value === code)?.label;
    return t(code, { defaultValue: fallback ?? code });
  };

  const fields = [
    { key: "student_card_number", value: student?.student_card_number },
    { key: "year_of_study", value: student?.year_of_study },
    { key: "phone_num", value: student?.phone_num },
    { key: "gender", value: student?.gender },
    { key: "identity", value: student?.identity },
    { key: "bachelor_degree", value: student?.bachelor_degree },
  ].filter((field) => Boolean(field.value));

  const previewIcon = editIcon || student?.profile_icon;

  const handleEnquiry = async () => {
    if (chatOpening) return;
    setChatOpening(true);
    const room = await openChatRoom({ type: "admin" });
    setChatOpening(false);
    if (room?.id) navigate(`/student/chat/${room.id}`);
  };

  useEffect(() => {
    setFollowingTotal(followingCount);
  }, [followingCount]);

  useEffect(() => {
    setSubscribedTotal(subscribedCount);
  }, [subscribedCount]);

  const fetchSubscribedEvents = useCallback(async () => {
    setSubscribedLoading(true);
    const collected: StudentEvent[] = [];
    for (let page = 1; page <= SUBSCRIPTION_MAX_PAGES; page += 1) {
      const resp = await GetSubscriptedEventsAPI(page);
      if (resp?.status !== 0 || !Array.isArray(resp.result)) break;
      const events = resp.result as StudentEvent[];
      collected.push(...events);
      if (events.length < SUBSCRIPTION_PAGE_SIZE) break;
    }
    setSubscribedEvents(collected);
    setSubscribedMap(
      Object.fromEntries(collected.map((item) => [item.details.id, true])),
    );
    setSubscribedLoading(false);
  }, []);

  useEffect(() => {
    fetchSubscribedEvents();
  }, [fetchSubscribedEvents]);

  const handleToggleFollow = async (societyId: string) => {
    if (pendingSocietyId) return;
    const current = followingMap[societyId] ?? true;
    const next = !current;
    setPendingSocietyId(societyId);
    setFollowingMap((prev) => ({ ...prev, [societyId]: next }));
    setFollowingTotal((prev) =>
      prev === null ? prev : prev + (next ? 1 : -1),
    );
    setTabError("");

    const resp = await FollowOrganizerOrNotAPI(current, societyId);
    setPendingSocietyId(null);
    if (resp?.status === 0) return;

    setFollowingMap((prev) => ({ ...prev, [societyId]: current }));
    setFollowingTotal((prev) =>
      prev === null ? prev : prev + (next ? -1 : 1),
    );
    setTabError(t("Something went wrong"));
  };

  const handleToggleSubscribe = async (eventId: string) => {
    if (pendingEventId) return;
    const current = subscribedMap[eventId] ?? true;
    const next = !current;
    setPendingEventId(eventId);
    setSubscribedMap((prev) => ({ ...prev, [eventId]: next }));
    setSubscribedTotal((prev) =>
      prev === null ? prev : prev + (next ? 1 : -1),
    );
    setTabError("");

    const resp = await SubscribeOrNotAPI(current, eventId);
    setPendingEventId(null);
    if (resp?.status === 0) {
      const countResp = await CountSubscriptedAPI();
      if (countResp?.status === 0 && typeof countResp.result === "number") {
        setSubscribedTotal(countResp.result);
      }
      return;
    }

    setSubscribedMap((prev) => ({ ...prev, [eventId]: current }));
    setSubscribedTotal((prev) =>
      prev === null ? prev : prev + (next ? -1 : 1),
    );
    setTabError(t("Something went wrong"));
  };

  const tabs: { key: AccountTab; label: string; count: number | null }[] = [
    {
      key: "societies",
      label: t("My Societies"),
      count: societies.length + pendingSocieties.length,
    },
    { key: "following", label: t("Following"), count: followingTotal },
    { key: "subscription", label: t("Subscription"), count: subscribedTotal },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-4 text-2xl font-bold text-gray-900">{t("Account")}</h1>

      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <StudentAvatar
            src={student?.profile_icon}
            name={displayName}
            size="lg"
          />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-extrabold text-gray-900">
              {displayName || t("Student")}
            </h2>
            <p className="truncate text-sm text-gray-500">
              {universityLabel(student?.university)}
            </p>
            <p className="truncate text-sm text-gray-500">
              {student?.university_email}
            </p>
          </div>
          <button
            type="button"
            onClick={openEditProfile}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#e08f0e]"
          >
            <Pencil size={15} />
            {t("Edit Profile")}
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row">
          <button
            type="button"
            onClick={openChangePassword}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 py-2 text-sm font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
          >
            <KeyRound size={15} />
            {t("Change Password")}
          </button>
          <button
            type="button"
            onClick={handleEnquiry}
            disabled={chatOpening}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 py-2 text-sm font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-60"
          >
            {chatOpening ? (
              <Loader2 size={15} className="animate-spin text-[#ffa217]" />
            ) : (
              <MessageCircle size={15} />
            )}
            {t("Enquiry")}
          </button>
          <button
            type="button"
            onClick={() => setLogoutVisible(true)}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-red-200 py-2 text-sm font-bold text-red-500 transition hover:border-red-300 hover:bg-red-50"
          >
            <LogOut size={15} />
            {t("Logout")}
          </button>
        </div>

        {(followingTotal !== null || subscribedTotal !== null) && (
          <div className="mt-4 flex gap-4 border-t border-gray-100 pt-4">
            <div className="flex-1 text-center">
              <div className="text-xl font-extrabold text-[#ffa217]">
                {followingTotal ?? 0}
              </div>
              <div className="text-xs font-semibold text-gray-500">
                {t("Following")}
              </div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-xl font-extrabold text-[#ffa217]">
                {subscribedTotal ?? 0}
              </div>
              <div className="text-xs font-semibold text-gray-500">
                {t("Subscription")}
              </div>
            </div>
          </div>
        )}

        {fields.length > 0 && (
          <div className="mt-4 grid gap-x-6 gap-y-3 border-t border-gray-100 pt-4 sm:grid-cols-2">
            {fields.map((field) => {
              const Icon = FIELD_ICONS[field.key];
              return (
                <div key={field.key} className="flex items-start gap-2">
                  <Icon size={16} className="mt-0.5 shrink-0 text-[#ffa217]" />
                  <div className="min-w-0">
                    <div className="text-xs text-gray-400">
                      {t(FIELD_LABELS[field.key])}
                    </div>
                    <div className="truncate text-sm font-semibold text-gray-900">
                      {String(field.value)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <StudentVerificationCard />

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {isSocietyMember && (
          <>
            <Link
              to="/student/my-events"
              className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition hover:border-[#ffa217]"
            >
              <CalendarDays size={18} className="shrink-0 text-[#ffa217]" />
              <span className="text-sm font-bold text-gray-900">
                {t("My Created Events")}
              </span>
            </Link>
            <Link
              to="/student/events/new"
              className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition hover:border-[#ffa217]"
            >
              <Plus size={18} className="shrink-0 text-[#ffa217]" />
              <span className="text-sm font-bold text-gray-900">
                {t("Create Event")}
              </span>
            </Link>
          </>
        )}
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center gap-1.5 border-b border-gray-100 px-3 py-2.5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTab(tab.key);
                setTabError("");
              }}
              aria-pressed={activeTab === tab.key}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition sm:text-sm ${
                activeTab === tab.key
                  ? "bg-[#fff3e0] text-[#ffa217]"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="text-[10px] font-bold opacity-70">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === "societies" &&
            (loading ? (
              <p className="py-4 text-center text-sm text-gray-400">
                <Loader2
                  size={16}
                  className="mx-auto animate-spin text-[#ffa217]"
                />
              </p>
            ) : societies.length === 0 && pendingSocieties.length === 0 ? (
              <AdminEmptyState
                iconType="MaterialCommunityIcons"
                iconName="account-group-outline"
                title={t("No societies yet")}
                message={t("Societies you join will appear here.")}
              />
            ) : (
              <div className="space-y-2.5">
                {societies.map((society) => (
                  <div
                    key={society.membership_id}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 p-3"
                  >
                    <SocietyAvatar
                      icon={society.icon ?? undefined}
                      name={society.name}
                      className="h-10 w-10 text-sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-gray-900">
                        {society.name}
                      </div>
                      {society.university && (
                        <div className="truncate text-xs text-gray-500">
                          {universityLabel(society.university)}
                        </div>
                      )}
                    </div>
                    {society.instagram_profile_url && (
                      <a
                        href={society.instagram_profile_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-xs font-semibold text-[#ffa217] hover:underline"
                      >
                        IG
                      </a>
                    )}
                  </div>
                ))}

                {pendingSocieties.map((society) => (
                  <div
                    key={society.membership_id}
                    className="flex items-center gap-3 rounded-xl border border-dashed border-[#ffd8a8] bg-[#fff8f0] p-3"
                  >
                    <SocietyAvatar
                      icon={society.icon ?? undefined}
                      name={society.name}
                      className="h-10 w-10 text-sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-gray-900">
                        {society.name}
                      </div>
                      {society.university && (
                        <div className="truncate text-xs text-gray-500">
                          {universityLabel(society.university)}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#ffa217]">
                      {t("Pending")}
                    </span>
                  </div>
                ))}
              </div>
            ))}

          {activeTab === "following" &&
            (loading ? (
              <p className="py-4 text-center text-sm text-gray-400">
                <Loader2
                  size={16}
                  className="mx-auto animate-spin text-[#ffa217]"
                />
              </p>
            ) : followings.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                {t("Societies you follow will appear here.")}
              </p>
            ) : (
              <div className="space-y-2.5">
                {followings.map((society, index) => {
                  const name =
                    pickLocalizedName(society, language) ??
                    society.name ??
                    society.id ??
                    "";
                  const societyId = society.id;
                  const followed = followingMap[societyId ?? ""] ?? true;
                  const pending = pendingSocietyId === societyId;
                  return (
                    <div
                      key={society.id ?? `following-${index}`}
                      className="flex items-center gap-3 rounded-xl border border-gray-100 p-3"
                    >
                      <StudentAvatar
                        src={society.society_icon ?? society.icon}
                        name={name}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                        {name}
                      </div>
                      {societyId && (
                        <button
                          type="button"
                          onClick={() => handleToggleFollow(societyId)}
                          disabled={pending}
                          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition disabled:opacity-60 ${
                            followed
                              ? "border-red-200 bg-white text-red-500 hover:border-red-300"
                              : "border-[#ffa217] bg-[#fff3e0] text-[#ffa217] hover:bg-[#ffe8c4]"
                          }`}
                        >
                          {pending ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : followed ? (
                            <Bell size={12} />
                          ) : (
                            <Plus size={12} />
                          )}
                          {followed ? t("Unfollow") : t("Follow")}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

          {activeTab === "subscription" &&
            (subscribedLoading ? (
              <p className="py-4 text-center text-sm text-gray-400">
                <Loader2
                  size={16}
                  className="mx-auto animate-spin text-[#ffa217]"
                />
              </p>
            ) : subscribedEvents.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                {t("Events you subscribe to will appear here.")}
              </p>
            ) : (
              <div className="space-y-2.5">
                {subscribedEvents.map((item) => {
                  const { details } = item;
                  const subscribed = subscribedMap[details.id] ?? true;
                  const pending = pendingEventId === details.id;
                  const covers = toImageList(details.event_images);
                  const date = formatDateRange(
                    details.start_date,
                    details.end_date,
                  );
                  const time = formatTimeRange(
                    details.start_time,
                    details.end_time,
                  );
                  return (
                    <div
                      key={details.id}
                      className="flex items-center gap-3 rounded-xl border border-gray-100 p-3"
                    >
                      {covers.length > 0 && (
                        <img
                          src={covers[0]}
                          alt=""
                          loading="lazy"
                          className="h-12 w-12 shrink-0 rounded-lg object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/student/events/${details.id}`}
                          state={{
                            event: { ...item, subscribed },
                          }}
                          className="line-clamp-2 text-sm font-bold text-gray-900 hover:text-[#ffa217]"
                        >
                          {localized(
                            details.title,
                            details.title_en,
                            language,
                          ) || t("Untitled Event")}
                        </Link>
                        {(date || details.location) && (
                          <div className="truncate text-xs text-gray-500">
                            {[date, time, details.location]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleSubscribe(details.id)}
                        disabled={pending}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold transition disabled:opacity-60 ${
                          subscribed
                            ? "border-red-200 bg-white text-red-500 hover:border-red-300"
                            : "border-[#ffa217] bg-[#fff3e0] text-[#ffa217] hover:bg-[#ffe8c4]"
                        }`}
                      >
                        {pending ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : subscribed ? (
                          <BookmarkCheck size={12} />
                        ) : (
                          <Bookmark size={12} />
                        )}
                        {subscribed ? t("Unsubscribe") : t("Subscribe")}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}

          {tabError && (
            <p className="mt-3 text-center text-xs font-semibold text-red-500">
              {tabError}
            </p>
          )}
        </div>
      </div>

      {/* Edit profile */}
      <Modal visible={editVisible} onClose={() => setEditVisible(false)}>
        <div className="max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
          <div className="mb-4 text-center text-lg font-bold text-gray-900">
            {t("Edit Profile")}
          </div>

          <div className="mb-5 flex flex-col items-center">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-[#ffa217] bg-[#fff8f0] transition hover:bg-[#fff3e0]"
            >
              {previewIcon ? (
                <img
                  src={previewIcon}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <CircleUser size={44} className="text-[#ffa217]" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                pickImage(e.target.files?.[0]);
                setEditInvalid(false);
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#ffa217] transition hover:text-[#e08f0e]"
            >
              <Camera size={15} />
              {t("Change photo")}
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("Username")}
              </label>
              <input
                type="text"
                value={editUsername}
                onChange={(e) => {
                  setEditUsername(e.target.value);
                  setEditInvalid(false);
                }}
                className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
                  editInvalid && !editUsername
                    ? "border-red-400"
                    : "border-gray-200 focus:border-[#ffa217]/50"
                }`}
                placeholder={t("Username")}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("Phone Number")}
              </label>
              <div className="flex gap-2">
                <select
                  value={editRegionCode}
                  onChange={(e) => {
                    setEditRegionCode(e.target.value);
                    setEditInvalid(false);
                  }}
                  className="w-28 shrink-0 rounded-xl border border-gray-200 bg-[#fff3e0]/50 px-3 py-2.5 text-sm text-gray-900 focus:border-[#ffa217]/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30"
                  aria-label={t("Region Code")}
                >
                  {COUNTRY_CODES.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.flag} {country.value}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={editPhone}
                  onChange={(e) => {
                    setEditPhone(e.target.value);
                    setEditInvalid(false);
                  }}
                  className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
                    editInvalid && !editPhone
                      ? "border-red-400"
                      : "border-gray-200 focus:border-[#ffa217]/50"
                  }`}
                  placeholder={t("Phone Number")}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setEditVisible(false)}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={saveProfile}
              disabled={savingProfile}
              className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
            >
              {savingProfile ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                t("Save")
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Verify new phone number before saving the profile */}
      <Modal
        visible={editPhoneVerifyVisible}
        onClose={() => setEditPhoneVerifyVisible(false)}
      >
        <div className="rounded-2xl bg-white p-5 shadow-lg">
          <div className="mb-1 text-center text-lg font-bold text-gray-900">
            {t("Verify new phone number")}
          </div>
          <p className="mb-4 text-center text-sm font-semibold text-[#ffa217]">
            {pendingPhone}
          </p>

          <label className="mb-1.5 block text-sm font-medium text-gray-700">
            {t("SMS code")}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={editPhoneCode}
            onChange={(e) => {
              setEditPhoneCode(e.target.value);
              setEditPhoneError("");
            }}
            className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
              editPhoneError
                ? "border-red-400"
                : "border-gray-200 focus:border-[#ffa217]/50"
            }`}
            placeholder={t("SMS code")}
          />
          {editPhoneError && (
            <p className="mt-2 text-center text-xs font-semibold text-red-500">
              {editPhoneError}
            </p>
          )}

          <button
            type="button"
            onClick={resendProfilePhoneCode}
            disabled={sendingPhoneCode}
            className="mt-3 w-full text-center text-sm font-semibold text-[#ffa217] transition hover:text-[#e08f0e] disabled:opacity-60"
          >
            {sendingPhoneCode ? t("Sending...") : t("Resend code")}
          </button>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setEditPhoneVerifyVisible(false)}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={verifyPhoneAndSave}
              disabled={verifyingPhoneCode}
              className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
            >
              {verifyingPhoneCode ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                t("VERIFY")
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Change password */}
      <Modal
        visible={passwordVisible}
        onClose={() => setPasswordVisible(false)}
      >
        <div className="rounded-2xl bg-white p-5 shadow-lg">
          <div className="mb-4 text-center text-lg font-bold text-gray-900">
            {t("Change Password")}
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("Old Password")}
              </label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  setPasswordInvalid(false);
                }}
                className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
                  passwordInvalid && !oldPassword
                    ? "border-red-400"
                    : "border-gray-200 focus:border-[#ffa217]/50"
                }`}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("New Password")}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordInvalid(false);
                }}
                className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
                  passwordInvalid && !newPassword
                    ? "border-red-400"
                    : "border-gray-200 focus:border-[#ffa217]/50"
                }`}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setPasswordVisible(false)}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={savePassword}
              disabled={savingPassword}
              className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
            >
              {savingPassword ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                t("Save")
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Generic alert */}
      <Modal visible={alertVisible} onClose={hideAlert}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-gray-900">
            {alertText}
          </div>
          <button
            type="button"
            onClick={hideAlert}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      {/* Logout confirm */}
      <Modal visible={logoutVisible} onClose={() => setLogoutVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <LogOut size={32} className="mx-auto mb-3 text-red-500" />
          <div className="mb-1 text-lg font-bold text-gray-900">
            {t("Logout Now?")}
          </div>
          <p className="mb-5 text-sm text-gray-500">
            {t("You'll be signed out of your account.")}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setLogoutVisible(false)}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white transition hover:bg-red-600"
            >
              {t("Logout")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StudentAccountPage;
